import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Instagram, Facebook, Menu, X, Gift } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const SESSION_DETAILS = [
  { label: "The Experience", to: "/experience" },
  { label: "Investment", to: "/investment" },
  { label: "Locations", to: "/locations" },
];
const CLIENT_HUB = [
  { label: "FAQ", to: "/faq" },
  { label: "Editing Queue", to: "/editing-queue" },
  { label: "Client Guide", to: "/client-guide" },
  { label: "Journal", to: "/journal" },
];
const NAV_LEFT = [
  { label: "Meet Jac", to: "/meet-jac" },
  { label: "Plan Your Session", dropdown: SESSION_DETAILS },
  { label: "Portfolio", to: "/portfolio" },
];
const NAV_RIGHT = [
  { label: "Studio Rental", to: "/studio-rental" },
  { label: "Client Hub", dropdown: CLIENT_HUB },
  { label: "Contact", to: "/contact" },
];

const IG_URL = "https://instagram.com/jacclairephotos";
const FB_URL = "https://facebook.com/jacclairephotos";

const NavItem = ({ to, label, dropdown }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  if (dropdown) {
    const active = dropdown.some((d) => location.pathname === d.to);
    return (
      <div
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="relative"
      >
        <button
          type="button"
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}-btn`}
          className={`group flex items-center gap-1.5 font-sans text-[12px] md:text-[13px] uppercase tracking-[0.24em] whitespace-nowrap transition-colors duration-300 ${
            active ? "text-ink" : "text-inksoft hover:text-ink"
          }`}
        >
          {label}
          <ChevronDown
            size={13}
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
          <span
            className={`absolute left-0 -bottom-1.5 h-px bg-sage transition-all duration-500 ${
              active ? "w-full" : "w-0 group-hover:w-full"
            }`}
          />
        </button>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25, ease: EASE }}
              className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50"
            >
              <div className="min-w-[220px] bg-cream border border-ink/10 shadow-[0_20px_60px_-20px_rgba(30,30,25,0.25)] py-3">
                {dropdown.map((d) => (
                  <Link
                    key={d.to}
                    to={d.to}
                    data-testid={`nav-dropdown-${d.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className="block px-6 py-2.5 font-serif italic text-[17px] text-inksoft hover:text-sage hover:bg-sagelight/40 transition-colors"
                  >
                    {d.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const active = location.pathname === to;
  return (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
      className={`group relative font-sans text-[12px] md:text-[13px] uppercase tracking-[0.24em] whitespace-nowrap transition-colors duration-300 ${
        active ? "text-ink" : "text-inksoft hover:text-ink"
      }`}
    >
      {label}
      <span
        className={`absolute left-0 -bottom-1.5 h-px bg-sage transition-all duration-500 ${
          active ? "w-full" : "w-0 group-hover:w-full"
        }`}
      />
    </Link>
  );
};

const Socials = ({ className = "", compact = false }) => {
  // Holiday-season glow: Nov (10) & Dec (11) only
  const month = new Date().getMonth();
  const isHolidaySeason = month === 10 || month === 11;
  return (
  <div className={`flex items-center gap-3 ${className}`}>
    <a
      href="https://book.usesession.com/i/zvTVBjRIv/gift-cards"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-gift"
      aria-label="Gift a session"
      className={`group relative inline-flex items-center justify-center gap-2 rounded-full border h-9 transition-all duration-300 ${
        compact ? "w-9 px-0" : "pl-3 pr-4"
      } ${
        isHolidaySeason
          ? "border-sage bg-sagelight text-sagedark hover:text-cream hover:bg-sage shadow-[0_0_20px_rgba(115,134,120,0.35)] animate-holiday-glow"
          : "border-sage/40 bg-sagelight/50 text-sage hover:text-cream hover:bg-sage hover:border-sage"
      }`}
    >
      {isHolidaySeason && (
        <span className="absolute -inset-px rounded-full bg-sage/20 blur-md pointer-events-none animate-pulse" />
      )}
      <Gift size={15} strokeWidth={1.8} className="relative" />
      {!compact && (
        <span className="relative font-sans text-[11px] uppercase tracking-[0.22em] whitespace-nowrap">Gift a session</span>
      )}
    </a>
    <a
      href={IG_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-instagram"
      aria-label="Instagram"
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-inksoft hover:text-cream hover:bg-ink hover:border-ink transition-colors duration-300"
    >
      <Instagram size={15} strokeWidth={1.8} />
    </a>
    <a
      href={FB_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-facebook"
      aria-label="Facebook"
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-inksoft hover:text-cream hover:bg-ink hover:border-ink transition-colors duration-300"
    >
      <Facebook size={15} strokeWidth={1.8} />
    </a>
  </div>
  );
};

const MobileDropdown = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full text-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`mobile-dropdown-${label.toLowerCase().replace(/\s+/g, "-")}-toggle`}
        className="w-full inline-flex items-center justify-center gap-1.5 font-sans text-[12px] uppercase tracking-[0.24em] text-ink py-2"
      >
        {label}
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="flex flex-col items-center gap-1 pt-1 pb-2">
              {items.map((d) => (
                <Link
                  key={d.to}
                  to={d.to}
                  data-testid={`mobile-dropdown-${d.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className="font-serif italic text-lg text-sage py-1"
                >
                  {d.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-cream transition-[padding,box-shadow,background-color] duration-300 ${
        scrolled
          ? "pt-2 md:pt-2 pb-2 md:pb-2 bg-cream/95 backdrop-blur-md shadow-[0_10px_30px_-15px_rgba(30,30,25,0.15)]"
          : "pt-4 md:pt-5 pb-2 md:pb-3"
      }`}
    >
      {/* Social icons — top-right on desktop only (hidden on mobile to prevent logo overlap) */}
      <Socials compact={scrolled} className={`hidden lg:flex absolute right-6 md:right-10 z-20 transition-[top] duration-300 ${scrolled ? "top-3 md:top-4" : "top-6 md:top-8"}`} />

      {/* Mobile menu toggle — floating top-left */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setMobileOpen((v) => !v)}
        className={`lg:hidden absolute left-6 md:left-10 z-20 text-ink transition-[top] duration-300 ${scrolled ? "top-3 md:top-4" : "top-6 md:top-8"}`}
        aria-label="Menu"
      >
        {mobileOpen ? <X size={26} /> : <Menu size={26} />}
      </button>

      {/* Desktop layout — logo dead-center; nav items evenly spread across each side */}
      <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center max-w-[1500px] mx-auto px-8 xl:px-16 gap-6 xl:gap-10">
        <div className="flex items-center justify-around gap-4">
          {NAV_LEFT.map((l) => (
            <NavItem key={l.label} {...l} />
          ))}
        </div>

        <Link to="/" data-testid="site-logo" className="block shrink-0 justify-self-center -my-2">
          <img
            src="/logo.png"
            alt="Jacqueline Claire Photography"
            className={`w-auto object-contain transition-[height] duration-300 ${scrolled ? "h-16 xl:h-20" : "h-36 xl:h-48"}`}
          />
        </Link>

        <div className="flex items-center justify-around gap-4">
          {NAV_RIGHT.map((l) => (
            <NavItem key={l.label} {...l} />
          ))}
        </div>
      </div>

      {/* Mobile / tablet — stacked logo, hamburger drawer */}
      <div className="lg:hidden flex justify-center">
        <Link to="/" data-testid="site-logo-mobile" className="block">
          <img
            src="/logo.png"
            alt="Jacqueline Claire Photography"
            className={`w-auto object-contain transition-[height] duration-300 ${scrolled ? "h-14 sm:h-16" : "h-28 sm:h-32"}`}
          />
        </Link>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="lg:hidden overflow-hidden bg-cream border-t border-ink/10 mt-4"
          >
            <div className="flex flex-col px-6 py-6 gap-2 items-center">
              {[...NAV_LEFT, ...NAV_RIGHT].map((l) =>
                l.dropdown ? (
                  <MobileDropdown key={l.label} label={l.label} items={l.dropdown} />
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className="font-sans text-[13px] uppercase tracking-[0.24em] text-ink py-2"
                  >
                    {l.label}
                  </Link>
                )
              )}

              {/* Gift a session + social icons — stacked & centered at the bottom of the mobile drawer */}
              <div className="pt-6 mt-4 border-t border-ink/10 w-full flex flex-col items-center gap-4">
                <a
                  href="https://book.usesession.com/i/zvTVBjRIv/gift-cards"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-gift"
                  aria-label="Gift a session"
                  className="group inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sagelight/50 text-sage hover:text-cream hover:bg-sage hover:border-sage h-10 pl-4 pr-5 transition-colors duration-300"
                >
                  <Gift size={15} strokeWidth={1.8} />
                  <span className="font-sans text-[11px] uppercase tracking-[0.22em] whitespace-nowrap">Gift a session</span>
                </a>
                <div className="flex items-center gap-3">
                  <a
                    href={IG_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-instagram"
                    aria-label="Instagram"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-inksoft hover:text-cream hover:bg-ink hover:border-ink transition-colors duration-300"
                  >
                    <Instagram size={15} strokeWidth={1.8} />
                  </a>
                  <a
                    href={FB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-facebook"
                    aria-label="Facebook"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-inksoft hover:text-cream hover:bg-ink hover:border-ink transition-colors duration-300"
                  >
                    <Facebook size={15} strokeWidth={1.8} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SiteHeader;
